// ============================================================================
// FormSpec — declarative form schema persisted on ewf.tbl_workflow_state.form_spec
// ============================================================================
// Source of truth for what the case-detail right-pane renders when the user
// clicks a workflow step in the rail. Edited in the workflow designer (Phase
// 5 enhancement) or via SQL seed today. Read by <DynamicForm /> at render
// time; never inlined as hard-coded form schemas in components.
//
// Mirrors the FSA template pattern (column_spec / additional_disclosures
// JSONB on fsa.tbl_template_schedule) — same idea, RecSuit shape.

// ----------------------------------------------------------------------------
// Field type primitives
// ----------------------------------------------------------------------------

export type FieldType =
  | 'text'
  | 'textarea'
  | 'number'
  | 'currency'
  | 'date'
  | 'datetime'
  | 'boolean'
  | 'select'
  | 'ref'              // FK picker — loads options from a master
  | 'security_select'  // multi-select checkbox list of THIS case's securities
  | 'grid';            // repeating sub-form (array of objects)

// ----------------------------------------------------------------------------
// Snapshot column targets (must match the DB columns on lit.tbl_case_state_data)
// ----------------------------------------------------------------------------

export type SnapshotColumn =
  | 'amount_primary'
  | 'amount_secondary'
  | 'event_date'
  | 'status_value'
  | 'ref_master_id'
  | 'flag_value'
  | 'category_value'
  | 'counterparty_name';

export type RefMasterType =
  | 'YARD'
  | 'VALUATOR'
  | 'AGENCY'
  | 'AUCTION_PLATFORM'
  | 'ADVOCATE'
  | 'COURT'
  | 'USER'
  | 'OTHER';

// ----------------------------------------------------------------------------
// Select option — used by 'select' fields
// ----------------------------------------------------------------------------

export interface SelectOption {
  value: string;
  label: string;
  /** Optional description shown beneath the option (in dropdowns that support it). */
  description?: string;
}

// ----------------------------------------------------------------------------
// Base field — shared by every field type
// ----------------------------------------------------------------------------

export interface FieldSpecBase {
  /** Unique key inside the form (and inside `data` JSONB). camelCase or snake_case. */
  key: string;
  /** Display label rendered above the input. */
  label: string;
  type: FieldType;
  /** Optional helper / description shown beneath the label. */
  description?: string;
  /** Field-level required flag — surfaces inline error on save when empty. */
  required?: boolean;
  /** Inline conditional. Expression evaluated against the current form values.
   *  Examples:  "ack_status != 'PENDING'", "outcome == 'SOLD'", "force_used == true"
   *  Supported operators: == != > >= < <= && ||
   *  Operands: field references (bare identifiers), string literals (single-quoted),
   *  numeric literals, boolean literals (true/false), null. */
  show_if?: string;
  /** Override the default field width — '60px' / '180px' / '50%' / 'full'. */
  width?: string;
  /** Maps the field's value to a denormalised column on tbl_case_state_data on save. */
  denorm_to?: SnapshotColumn;
  /** For ref/grid-row-ref fields, optional master-type tag stored to ref_master_type. */
  denorm_to_type?: RefMasterType;
  /** For ref fields, when denorm_to=ref_master_id, where the display name lands. */
  denorm_to_name?: 'ref_master_name';
  /** Computed field — value derived from other fields and persisted on save.
   *  Always read-only. Currently supports a single date-shift form:
   *    "{date_field} + {days_field}"  →  dateAddDays(dateField, daysField)
   *  Extend the evaluator as more shapes appear. */
  computed?: string;
  /** Force read-only render. Computed fields are always read-only regardless. */
  readonly?: boolean;
  /** Dynamic label overrides — first entry whose `if` expression (same grammar
   *  as show_if) evaluates true against the current form values wins; otherwise
   *  `label` is used. Lets one stored field relabel by context, e.g.
   *  "Place of Arbitration" → "Arbitration Sitting" when sitting_type=='ONLINE'. */
  dynamic_label?: Array<{ if: string; label: string }>;
}

// ----------------------------------------------------------------------------
// Type-specific field variants
// ----------------------------------------------------------------------------

export interface TextFieldSpec extends FieldSpecBase {
  type: 'text';
  max_length?: number;
  min_length?: number;
  pattern?: string;            // RFC: regex source string
  placeholder?: string;
}

export interface TextareaFieldSpec extends FieldSpecBase {
  type: 'textarea';
  rows?: number;
  max_length?: number;
  placeholder?: string;
}

export interface NumberFieldSpec extends FieldSpecBase {
  type: 'number';
  min?: number;
  max?: number;
  step?: number;
  placeholder?: string;
}

export interface CurrencyFieldSpec extends FieldSpecBase {
  type: 'currency';
  min?: number;
  max?: number;
  currency_code?: string;      // 'INR' default; reserved for future multi-currency
  placeholder?: string;
}

export interface DateFieldSpec extends FieldSpecBase {
  type: 'date';
  min?: string;                // ISO yyyy-mm-dd
  max?: string;
}

export interface DatetimeFieldSpec extends FieldSpecBase {
  type: 'datetime';
  min?: string;                // ISO datetime
  max?: string;
}

export interface BooleanFieldSpec extends FieldSpecBase {
  type: 'boolean';
  /** Render hint — 'checkbox' (default) | 'toggle' | 'yesno_radio'. */
  display?: 'checkbox' | 'toggle' | 'yesno_radio';
}

export interface SelectFieldSpec extends FieldSpecBase {
  type: 'select';
  options: Array<SelectOption | string>;
  /** Render hint — 'dropdown' (default) | 'radio'. */
  display?: 'dropdown' | 'radio';
}

export interface RefFieldSpec extends FieldSpecBase {
  type: 'ref';
  /** Where to load options from. Examples:
   *    'mst.tbl_yard'
   *    'mst.tbl_valuator'
   *    'admin.tbl_user_cache'
   *  Mapped to a service call by the DynamicForm at runtime. */
  options_source: string;
  /** Optional server-side filter expression (SQL fragment or service param key=val). */
  options_filter?: string;
}

/**
 * Multi-select of the current case's securities (lit.tbl_case_security). Renders
 * one checkbox per linked asset; the stored value is a string[] of the selected
 * `securityId`s. Used by the REPOSSESSED state to record which financed assets
 * were physically seized. Requires the form's seedContext to carry `caseId`.
 */
export interface SecuritySelectFieldSpec extends FieldSpecBase {
  type: 'security_select';
  /** When true (default), pre-ticks the case's in-scope securities on first
   *  load of a fresh entry. Set false to start with nothing ticked. */
  auto_select_in_scope?: boolean;
}

/** A field inside a grid row — same primitives, but no nested grids. */
export type GridRowFieldSpec =
  | TextFieldSpec
  | TextareaFieldSpec
  | NumberFieldSpec
  | CurrencyFieldSpec
  | DateFieldSpec
  | DatetimeFieldSpec
  | BooleanFieldSpec
  | SelectFieldSpec
  | RefFieldSpec;

export interface GridFieldSpec extends FieldSpecBase {
  type: 'grid';
  row_fields: GridRowFieldSpec[];
  min_rows?: number;
  max_rows?: number;
  add_label?: string;
  empty_message?: string;
  /** Optional: which row_field acts as the row's display label in collapsed view. */
  row_label_field?: string;
  /** Optional: name of a context resolver that pre-fills rows on user click.
   *  Resolver receives the runtime context (e.g. caseId) and returns
   *  GridRowValues[]. Currently supported: 'case_parties'. */
  seed_from?: string;
  /** Optional: label for the seed-from button. */
  seed_label?: string;
  /** Optional: row key used to de-dup when re-running seed (e.g. 'party_id').
   *  When provided, repeat clicks of "Pre-fill" only append rows whose key is
   *  not already present in the grid. */
  seed_dedupe_key?: string;
  /** Optional: ordered list of row_field keys treated as a per-row mini stepper.
   *  Each step is "done" when the corresponding cell is non-empty. Rendered as
   *  three colored dots/cell-group on each row. */
  row_stepper?: string[];
  /** When true, rows are always editable inline (no popout editor / no Edit
   *  button per row). Add Row appends a blank row directly; cells render as
   *  inputs. Best for small fast-entry grids (Date + Notes). Default false. */
  inline_edit?: boolean;
}

// ----------------------------------------------------------------------------
// The unified FieldSpec union
// ----------------------------------------------------------------------------

export type FieldSpec =
  | TextFieldSpec
  | TextareaFieldSpec
  | NumberFieldSpec
  | CurrencyFieldSpec
  | DateFieldSpec
  | DatetimeFieldSpec
  | BooleanFieldSpec
  | SelectFieldSpec
  | RefFieldSpec
  | SecuritySelectFieldSpec
  | GridFieldSpec;

// ----------------------------------------------------------------------------
// Validation rule (declarative, evaluated at save)
// ----------------------------------------------------------------------------

export interface ValidationRule {
  /** Stable identifier so the UI can show / dismiss the same rule reliably. */
  id: string;
  /** Boolean expression evaluated against the form values. Same operator set as show_if. */
  rule: string;
  /** Human-readable message displayed when the rule evaluates to FALSE. */
  message: string;
  /** error blocks save; warning is informational (drift-style, matches FSA convention). */
  severity?: 'error' | 'warning';
}

// ----------------------------------------------------------------------------
// Documents attachment policy
// ----------------------------------------------------------------------------

export interface DocumentsPolicy {
  /** Allowed document_type values for uploads tied to this state entry. */
  allowed_types?: string[];
  /** Optional helper text shown above the upload area. */
  description?: string;
}

// ----------------------------------------------------------------------------
// The top-level FormSpec
// ----------------------------------------------------------------------------

/**
 * A grouped section of fields. Optional alternative to a flat `fields[]` —
 * when `sections[]` is present on a FormSpec, the renderer uses it INSTEAD of
 * the top-level `fields[]`. Backward compatible: legacy specs without
 * `sections` still render via `fields[]`.
 */
export interface SectionSpec {
  /** Stable id (used as React key + scroll target). */
  key: string;
  /** Section title rendered as a sub-header. */
  title: string;
  /** Optional helper line beneath the title. */
  description?: string;
  /** Layout for fields inside this section (overrides FormSpec.layout). */
  layout?: 'single' | 'two_col' | 'auto';
  /** Fields inside the section. */
  fields: FieldSpec[];
  /**
   * Reusable workflow-widget marker. When set, the renderer draws the section
   * with a dedicated widget component instead of the generic field grid, while
   * validation/value plumbing still runs against the materialised `fields[]`.
   *   'notice'      — NoticeSection (recipients + service/delivery).
   *   'case_status' — CaseStatusSection (proceedings log + inline award).
   *   'accounts'     — embedded self-managing Accounts component (empty fields[]).
   *   'respondents'  — embedded self-managing Respondents component (empty fields[]).
   *   'case_details' — embedded self-managing Case Details / filing component.
   */
  render_as?: 'notice' | 'case_status' | 'accounts' | 'respondents' | 'case_details';
  /**
   * Compact notice-widget config. Present on authored specs with
   * `type: 'notice'`; the materialiser (materializeFormSpec) expands it into
   * `fields[]` + `render_as: 'notice'`. See NoticeConfig.
   */
  notice?: NoticeConfig;
  /**
   * Compact case-status-update widget config. Present on authored specs with
   * `type: 'case_status_update'`; the materialiser expands it into a repeatable
   * status-updates grid. Reusable across any workflow with a "repeat until X"
   * phase (hearings, postings, follow-ups). See CaseStatusUpdateConfig.
   */
  case_status_update?: CaseStatusUpdateConfig;
  /** Compact Accounts-widget config (embeds the self-managing Accounts UI). */
  accounts?: EmbeddedWidgetConfig;
  /** Compact Respondents-widget config (embeds the self-managing Respondents UI). */
  respondents?: EmbeddedWidgetConfig;
  /** Compact Case-Details-widget config (embeds the per-type filing-details UI). */
  case_details?: EmbeddedWidgetConfig;
  /** Tab label when the parent FormSpec is `tabbed` (defaults to `title`). */
  tab_label?: string;
  /** Groups sections into a shared tab (tabbed FormSpec). Sections with the same
   *  `tab` value render stacked under one tab — e.g. Accounts + Respondents as a
   *  single "Accounts & Respondents" tab. Defaults to the section's own `key`. */
  tab?: string;
  /**
   * Authoring shorthand — a section written as `{ type: '…', …config }` is
   * expanded by the materialiser. Not read by the renderer directly.
   */
  type?: 'notice' | 'case_status_update' | 'accounts' | 'respondents' | 'case_details';
}

/**
 * Config for the embedded self-managing case widgets (Accounts, Respondents).
 * These persist through their own REST endpoints, NOT the form's save cycle, so
 * the config only carries presentation hints. The concrete component is resolved
 * at render time via the embeddedSectionRegistry.
 */
export interface EmbeddedWidgetConfig {
  /** Section / tab title override. */
  title?: string;
  /** Optional helper line. */
  description?: string;
}

/**
 * Compact configuration for the reusable Case Status Update widget. Expands into
 * a repeatable "status updates" grid (one row per update — date, status, summary,
 * next date, remarks) persisted in the state-data JSONB. Drop into any workflow's
 * repeatable stage (arbitration hearings, court postings, recovery follow-ups).
 */
export interface CaseStatusUpdateConfig {
  /** Section header, e.g. "Hearings" / "Case Status Updates". */
  title?: string;
  /** Optional helper line under the header. */
  description?: string;
  /** Label for the per-update status/outcome select (default "Status"). */
  status_label?: string;
  /** Options for the status/outcome select (e.g. Hearing Held / Adjourned / Order Passed). */
  status_options?: SelectOption[];
  /** Label for the "next posting / hearing date" field (default "Next Date"). */
  next_date_label?: string;
  /** Label for the free-text summary field (default "Summary"). */
  summary_label?: string;
  /** Label for the per-update date column (default "Date"; arbitration → "Hearing Date"). */
  date_label?: string;
  /** Additional per-update columns appended after the standard ones. */
  extra_fields?: GridRowFieldSpec[];
  /**
   * When true, an inline Award Details block is revealed once any update row's
   * status equals `awarded_value`. The Award is a single case-level record
   * (flat `award_*` keys), not a per-row field. Folds the former Award stage in.
   */
  award?: boolean;
  /** Status option value that triggers the Award block (default 'AWARDED'). */
  awarded_value?: string;
}

/**
 * Compact configuration for the reusable Notice widget. One config expands into
 * the full standard notice layout (notice-detail fields + recipient service &
 * delivery grid + optional publications / replies / rejoinders + remarks),
 * giving every workflow the same notice UI with small per-workflow overrides.
 */
export interface NoticeConfig {
  /** Notice-template code (mst.tbl_notice_template) driving the in-widget
   *  "Generate Notice" button. Omit to hide the button. */
  notice_code?: string;
  /** Widget header label, e.g. "§13(2) Demand Notice". */
  title?: string;
  /** Optional helper line under the header. */
  description?: string;
  /** Common notice fields to include from the built-in standard set, by key.
   *  Recognised: notice_number, notice_date, outstanding_amount,
   *  overdue_amount, amount_demanded, due_date. Defaults to
   *  [notice_number, notice_date, outstanding_amount, overdue_amount] when
   *  neither `standard_fields` nor `detail_fields`/`custom_fields` is given. */
  standard_fields?: string[];
  /** Workflow-specific extra fields appended after the standard ones
   *  (e.g. Amount Required to Regularise, Regularisation Due Date). */
  custom_fields?: FieldSpec[];
  /** Legacy: full explicit list of notice-detail fields. Superseded by
   *  `standard_fields` + `custom_fields`; still honoured for older specs. */
  detail_fields?: FieldSpec[];
  /** Party roles offered in each recipient row's Role select + prefill.
   *  Defaults to PRIMARY / CO_BORROWER / GUARANTOR. */
  recipient_roles?: string[];
  /** Per-recipient dispatch / delivery columns (mode, dispatched, tracking,
   *  delivery status, delivery date, POD). Default true. */
  service_tracking?: boolean;
  /** Add a multi-row Newspaper Publications block (fallback service). */
  publication?: boolean;
  /** Add a Borrower Replies block (recipient-picker + reply details). */
  replies?: boolean;
  /** Add a Company Rejoinders block (reply-to picker + content). */
  rejoinders?: boolean;
  /** Add a free-text Remarks field at the foot. Default true. */
  remarks?: boolean;
  /** Minimum recipient rows required (default 1). */
  min_recipients?: number;
}

export interface FormSpec {
  /** Override panel header for this form (falls back to workflow state's panel_title). */
  panel_title?: string;
  /** Optional intro paragraph rendered above the fields. */
  description?: string;
  /** Layout hint — 'single' (one column), 'two_col' (auto-break), 'auto' (default). */
  layout?: 'single' | 'two_col' | 'auto';
  /** Flat field list (legacy / simple forms). */
  fields?: FieldSpec[];
  /** Grouped sections (preferred for multi-part forms). Takes precedence over `fields`. */
  sections?: SectionSpec[];
  /** When true, sections render as a horizontal tab strip (one tab per section)
   *  instead of stacked panels. Tab label = `section.tab_label ?? section.title`.
   *  Used for the Filing stage (Details / Accounts & Respondents / Hearings). */
  tabbed?: boolean;
  /** Cross-field validations evaluated at save. */
  validations?: ValidationRule[];
  /** Document upload policy for this state entry. */
  documents?: DocumentsPolicy;
}

// ----------------------------------------------------------------------------
// Form values + helpers
// ----------------------------------------------------------------------------

/**
 * Form values — flat record by field key. Grid fields hold an array of nested
 * value objects. Everything is `unknown` because the runtime shape varies per
 * field type; consumers narrow via the field spec.
 */
export type FormValues = Record<string, unknown>;

/** A grid row's value shape — by row_field key. */
export type GridRowValues = Record<string, unknown>;

// ----------------------------------------------------------------------------
// Type guards
// ----------------------------------------------------------------------------

export const isGridField = (f: FieldSpec): f is GridFieldSpec => f.type === 'grid';
export const isRefField  = (f: FieldSpec | GridRowFieldSpec): f is RefFieldSpec => f.type === 'ref';
export const isSelectField = (f: FieldSpec | GridRowFieldSpec): f is SelectFieldSpec => f.type === 'select';

// ----------------------------------------------------------------------------
// Helpers
// ----------------------------------------------------------------------------

/** Returns the flat list of all fields across sections + top-level fields. */
export function collectAllFields(spec: FormSpec): FieldSpec[] {
  if (spec.sections && spec.sections.length > 0) {
    return spec.sections.flatMap((s) => s.fields ?? []);
  }
  return spec.fields ?? [];
}
